import _ from 'lodash';

import type { IndicatorFlatFields } from './types';
import { ConformanceIndicatorRating, ConformanceTrend } from './types';

export type IndicatorThresholds = {
  LowerAppetiteNum?: number | null;
  LowerToleranceNum?: number | null;
  TargetValueTxt?: string | null;
  UpperAppetiteNum?: number | null;
  UpperToleranceNum?: number | null;
};

export type IndicatorResultTarget = {
  TargetValueNum?: number | null;
  TargetValueTxt?: string | null;
};

export const conformanceIndicatorRating = (
  indicator: IndicatorThresholds,
  indicatorResult: IndicatorResultTarget = {}
): ConformanceIndicatorRating => {
  const { TargetValueNum = null, TargetValueTxt = null } = indicatorResult;
  const {
    TargetValueTxt: targetTxt = '',
    UpperToleranceNum,
    LowerToleranceNum,
    UpperAppetiteNum,
    LowerAppetiteNum,
  } = indicator;

  let result = ConformanceIndicatorRating.NotSet;

  if (TargetValueNum !== null) {
    if (
      UpperToleranceNum === null &&
      LowerToleranceNum === null &&
      LowerAppetiteNum === null &&
      UpperAppetiteNum === null
    ) {
      return ConformanceIndicatorRating.NotSet;
    }

    if (!_.isNil(LowerToleranceNum)) {
      if (TargetValueNum < LowerToleranceNum) {
        return ConformanceIndicatorRating.Outside;
      }
    }
    if (!_.isNil(LowerAppetiteNum)) {
      if (TargetValueNum < LowerAppetiteNum) {
        return ConformanceIndicatorRating.OutsideAppetite;
      }
    }
    if (!_.isNil(UpperToleranceNum)) {
      if (TargetValueNum > UpperToleranceNum) {
        return ConformanceIndicatorRating.Outside;
      }
    }
    if (!_.isNil(UpperAppetiteNum)) {
      if (TargetValueNum > UpperAppetiteNum) {
        return ConformanceIndicatorRating.OutsideAppetite;
      }
    }

    return ConformanceIndicatorRating.Within;
  }
  if (TargetValueTxt) {
    // compare txt stripped whitespace and check case insensitive.
    result =
      TargetValueTxt.replace(/\s+/g, '').localeCompare(
        (targetTxt || '').replace(/\s+/g, ''),
        undefined,
        { sensitivity: 'base' }
      ) === 0
        ? ConformanceIndicatorRating.Within
        : ConformanceIndicatorRating.Outside;
  }

  return result;
};

export const getConformanceTrendRating = (
  indicator: IndicatorThresholds,
  results: IndicatorResultTarget[]
): ConformanceTrend | null => {
  if (results.length < 2) {
    return null;
  }
  const currentRating = conformanceIndicatorRating(indicator, results[0]);
  const previousRating = conformanceIndicatorRating(indicator, results[1]);
  if (currentRating === previousRating) {
    return ConformanceTrend.Stable;
  }

  const order = [
    ConformanceIndicatorRating.Outside,
    ConformanceIndicatorRating.OutsideAppetite,
    ConformanceIndicatorRating.Within,
  ];

  return order.indexOf(currentRating) > order.indexOf(previousRating)
    ? ConformanceTrend.Improving
    : ConformanceTrend.Deteriorating;
};

export const conformanceRatingFromResults = (
  data: Pick<
    IndicatorFlatFields,
    | 'orderedResults'
    | 'LowerToleranceNum'
    | 'TargetValueTxt'
    | 'UpperToleranceNum'
  >
) => {
  return conformanceIndicatorRating(data, data.orderedResults[0]);
};

/**
 * Numeric core of {@link calculatePercentageDifference}. Returns the signed
 * percentage change as a number, or `null` when there is no meaningful value
 * (division by zero) — the case the formatter renders as `"-"`.
 *
 * Used directly by the results-table sort comparator so ordering matches the
 * displayed value without re-parsing the formatted string. Preserves the
 * RSP-3985 behaviour: sub-display-precision movements collapse to `0`.
 */
export const percentageDifferenceValue = (
  currentValue: number,
  previousValue: number
): number | null => {
  if (currentValue === previousValue) {
    return 0;
  }

  if (previousValue === 0) {
    return null;
  }

  const magnitude = Math.abs((1 - currentValue / previousValue) * 100);
  // When the magnitude rounds below display precision (2dp), treat it as 0 so
  // the formatter renders "0.00%" rather than "-0.00%" on a non-change.
  if (magnitude < 0.005) {
    return 0;
  }

  return previousValue < currentValue ? magnitude : -magnitude;
};

export const calculatePercentageDifference = (
  currentValue: number,
  previousValue: number
): string => {
  const value = percentageDifferenceValue(currentValue, previousValue);
  if (value === null) {
    return '-';
  }

  return `${value.toFixed(2)}%`;
};
